// src/components/AppLayout.js
import React, { useEffect, useState } from "react";
import { Layout, Menu, Segmented, message, Grid } from "antd";
import {
  UserOutlined,
  CarOutlined,
  EuroOutlined,
  UnorderedListOutlined,
  LogoutOutlined,
  SettingOutlined,
  CrownOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import logo from "../assets/logo.png";
import { getPlanningModeLabels } from "../utils/planningMode";
import axios from "../api/axios";

const { Header, Sider, Content } = Layout;

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  layoutBg: "#F4FBFC",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

const LS_TOURS_KEY = "routing.cachedTours";
const LS_REPORT_CTX_KEY = "routing.cachedReportContext";
const LS_RUN_STATE_KEY = "routing.runState";

const AppLayout = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (_) {
      return null;
    }
  });

  useEffect(() => {
    document.title = "Patrick Sauter – Smart Routing";
  }, []);

  useEffect(() => {
    const syncUser = () => {
      try {
        setUser(JSON.parse(localStorage.getItem("user")));
      } catch (_) {
        setUser(null);
      }
    };

    window.addEventListener("storage", syncUser);
    window.addEventListener("planning-mode-updated", syncUser);

    return () => {
      window.removeEventListener("storage", syncUser);
      window.removeEventListener("planning-mode-updated", syncUser);
    };
  }, []);

  const labels = getPlanningModeLabels(user?.planningMode);
  const tenderMode = user?.planningMode === "TENDER";

  const handleTenderWorkspaceChange = async (workspace) => {
    try {
      const res = await axios.put(`/users/me/tender-workspace/${workspace}`);
      localStorage.setItem("user", JSON.stringify(res.data));
      localStorage.removeItem(LS_TOURS_KEY);
      localStorage.removeItem(LS_REPORT_CTX_KEY);
      localStorage.removeItem(LS_RUN_STATE_KEY);
      setUser(res.data);
      window.dispatchEvent(new Event("planning-mode-updated"));
      navigate(location.pathname);
      window.location.reload();
    } catch {
      message.error("Ausschreibungs-Reiter konnte nicht gewechselt werden.");
    }
  };
  const baseMenu = [
    { key: "passengers", icon: <UserOutlined />, label: "Passagiere" },
    { key: "buses", icon: <CarOutlined />, label: labels.vehiclePlural },
    { key: "costfactors", icon: <EuroOutlined />, label: "Kostenfaktoren" },
    { key: "planning-settings", icon: <ClockCircleOutlined />, label: "Planungsvorgaben" },
    { key: "routes", icon: <UnorderedListOutlined />, label: "Routen" },
    { key: "results", icon: <UnorderedListOutlined />, label: "Ergebnisse" },
    { key: "maps", icon: <EnvironmentOutlined />, label: "Kartenübersicht" },
    { key: "mobile", icon: <CarOutlined />, label: "Mobile Übersicht" },
    { key: "drivers", icon: <UserOutlined />, label: "Fahrerzugänge" },
    { key: "account", icon: <SettingOutlined />, label: "Account" },
    { key: ".", icon: <EuroOutlined />, label: "Mein Tarif" },
    { key: "tariffs", icon: <EuroOutlined />, label: "Kapazität ändern" },
  ];

  const menuItems = [
    ...baseMenu,
    ...(user?.roles?.includes("ADMIN")
      ? [{ key: "admin", icon: <CrownOutlined />, label: "Admin" }]
      : []),
    { type: "divider" },
    { key: "logout", icon: <LogoutOutlined />, label: "Abmelden" },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === "logout") {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem(LS_TOURS_KEY);
      localStorage.removeItem(LS_REPORT_CTX_KEY);

      navigate("/");
      return;
    }

    navigate(key);
  };

  const seg = location.pathname.replace(/^\/app\/?/, "").split("/")[0];
  const selectedKey = seg === "" ? "." : seg;

  return (
    <Layout style={{ minHeight: "100vh", background: COLORS.layoutBg }}>
      <Sider
        breakpoint="lg"
        collapsedWidth={0}
        zeroWidthTriggerStyle={{ top: 12, zIndex: 20 }}
        width={250}
        style={{
          background: COLORS.primaryDark,
          boxShadow: "2px 0 12px rgba(31, 78, 87, 0.12)",
        }}
      >
        <div
          style={{
            height: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "14px 18px",
            borderBottom: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <Link
            to="/app"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              textDecoration: "none",
            }}
          >
            <img
              src={logo}
              alt="Patrick Sauter Smart Routing"
              style={{
                maxHeight: 44,
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          </Link>
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            background: COLORS.primaryDark,
            borderInlineEnd: "none",
            paddingTop: 12,
          }}
        />
      </Sider>

      <Layout style={{ background: COLORS.layoutBg, minWidth: 0 }}>
        <Header
          style={{
            height: 64,
            background: "#FFFFFF",
            color: COLORS.text,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: screens.lg ? "0 28px" : "0 12px 0 56px",
            borderBottom: `1px solid ${COLORS.border}`,
            boxShadow: "0 2px 10px rgba(31, 78, 87, 0.05)",
          }}
        >
          {/* Nur Branding, kein Seitentitel mehr */}
          <div
            style={{
              fontSize: 15,
              color: COLORS.muted,
              fontWeight: 500,
            }}
          >
            {screens.lg ? "Patrick Sauter Smart Routing" : "Smart Routing"}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {tenderMode && (
              <Segmented
                value={user?.activeTenderWorkspace || 1}
                onChange={handleTenderWorkspaceChange}
                options={[1, 2, 3].map((workspace) => ({
                  label: `Ausschreibung ${workspace}`,
                  value: workspace,
                }))}
              />
            )}
            <div
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 999,
                background: COLORS.primary,
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {labels.modeName}
            </div>
          </div>
        </Header>

        <Content
          style={{
            margin: "clamp(8px, 2vw, 24px)",
            padding: "clamp(12px, 2vw, 24px)",
            background: "#FFFFFF",
            minHeight: "calc(100vh - 112px)",
            borderRadius: 16,
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 8px 24px rgba(31, 78, 87, 0.06)",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
